// ============================================================================
// CLEO'S SECRET — Plan Checkout (Phase 3, UI slice 2a — renderable state)
// File: src/pages/PlanCheckout.jsx
//
// The card-collection surface. Patient lands here after PlanPreview "Accept
// as recommended". Stripe Elements <PaymentElement> renders inline against
// a brand-matched flat appearance (no rounded corners — Cleo's Secret is a
// flat-edge brand).
//
// Slice 2a (this file): renders to a complete visual surface — Stripe
// Elements live with a real PaymentIntent (test mode), but the Pay button
// is STUBBED to show the meditative "Securing your enrolment..." overlay
// for one diamond rotation, then dismisses with a "demo complete" inline
// banner. No real charge. No call to /api/program-plan-confirm.
//
// Slice 2b (next): wires stripe.confirmPayment() + /api/program-plan-confirm
// + EnrolmentSuccess.jsx.
//
// Layout: CSS Grid with auto-fit/minmax(380px, 1fr). At >= ~780px viewport
// renders two columns (summary | card form). Below that, single column.
// No JS viewport hook needed.
//
// Inputs (props from App.jsx routePayload, set by PlanPreview's handleAccept):
//   recommendationId, packageId, paymentOption, serviceEdits, addedServices
//
// If any required prop is missing (direct URL navigation, no PlanPreview
// step), routes back to PlanPreview with a calm one-line redirectMessage.
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  FF, P, T,
  Eyebrow,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';

// loadStripe at module level (Stripe's recommended pattern — script fetches
// once per app lifetime, not on every render). Lazy: doesn't fetch until
// first Elements mount.
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const TIER_NAME = { silver: 'Silver', gold: 'Gold', black: 'Black' };
const DIAMOND_ROTATION_SEC = 6;

// Brand-matched Stripe Elements appearance. Flat theme + palette overrides +
// borderRadius:0 (explicit per the Cleo flat-edge brand call). Inputs wear
// the SAND3 hairline; focus state goes BR. Labels Cinzel-flavoured.
function buildAppearance() {
  return {
    theme: 'flat',
    variables: {
      colorPrimary: P.GO,
      colorBackground: P.PAP,
      colorText: P.INK,
      colorTextSecondary: P.TF,
      colorDanger: P.RED,
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontSizeBase: '16px',
      spacingUnit: '4px',
      borderRadius: '0px',
    },
    rules: {
      '.Input': {
        border: '1px solid ' + P.SAND3,
        boxShadow: 'none',
        padding: '14px 14px',
        fontSize: '16px',
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        color: P.INK,
      },
      '.Input:focus': {
        border: '1.5px solid ' + P.GO,
        boxShadow: 'none',
        outline: 'none',
      },
      '.Input--invalid': {
        border: '1px solid ' + P.RED,
        color: P.INK,
      },
      '.Label': {
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '11px',
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        color: P.BR2,
        fontWeight: '600',
      },
      '.Tab': {
        border: '1px solid ' + P.SAND3,
        boxShadow: 'none',
      },
      '.Tab:hover': {
        border: '1px solid ' + P.GO,
        color: P.BR2,
      },
      '.Tab--selected': {
        border: '1.5px solid ' + P.GO,
        backgroundColor: 'rgba(201, 169, 97, 0.06)',
      },
    },
  };
}

// ─── Formatters (mirror PlanPreview) ───
function formatDollars(cents) {
  if (cents == null) return '';
  return new Intl.NumberFormat('en-AU').format(Math.floor(cents / 100));
}
function formatAUDFull(cents) {
  if (cents == null) return '';
  return '$' + formatDollars(cents);
}
function shortDateFromOffset(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

// ─── Diamond glyph (static, used in summary list bullets) ───
function Diamond({ size = 8, color, opacity = 1 }) {
  return (
    <span style={{
      display: 'inline-block', width: size, height: size,
      background: color || P.BR, transform: 'rotate(45deg)', opacity,
      flexShrink: 0,
    }} aria-hidden="true" />
  );
}

// ─── Meditative rotating diamond (uses cleo-rotate-diamond keyframes) ───
function MeditativeDiamond({ size = 56, color }) {
  return (
    <div style={{
      width: size, height: size,
      background: color || P.GO,
      animation: 'cleo-rotate-diamond ' + DIAMOND_ROTATION_SEC + 's linear infinite',
    }} aria-hidden="true" />
  );
}

// ─── Calm CTA (matches PlanPreview's pattern — kept local to avoid premature
//     extraction; will be lifted to design-tokens once a third surface needs it) ───
function CalmCTA({ label, onClick, disabled = false, fullWidth = true }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={function() { setHover(true); }}
      onMouseLeave={function() { setHover(false); }}
      style={{
        width: fullWidth ? '100%' : 'auto',
        padding: '18px 24px',
        background: hover && !disabled ? 'rgba(201, 169, 97, 0.06)' : 'transparent',
        border: '1.5px solid ' + (disabled ? P.SAND3 : P.GO),
        color: disabled ? P.TF : P.BR2,
        fontFamily: FF,
        fontSize: 'clamp(17px, 1.4vw, 20px)',
        fontStyle: 'italic',
        fontWeight: 400,
        letterSpacing: '-0.005em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.18s ease',
        textAlign: 'center',
      }}
    >{label}</button>
  );
}

// ─── Loading overlays ───────────────────────────────────────────────────
// Three distinct moments, three distinct copies. All sand surface, all
// italic Cormorant in BR. The middle one (submit → Stripe response) gets
// the meditative rotating diamond — the other two get a static ankh.

function PreparingOverlay() {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: P.SAND, zIndex: 1000,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 24,
    }}>
      <Ankh size={48} color={P.GO} opacity={0.55} />
      <div style={{ ...T.bodyMdItalic, color: P.BR2 }}>Preparing your checkout...</div>
    </div>
  );
}

function SecuringOverlay() {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: P.SAND, zIndex: 1000,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 36,
    }}>
      <MeditativeDiamond size={56} color={P.GO} />
      <div style={{
        ...T.bodyLg, color: P.BR2, fontStyle: 'italic', fontWeight: 500,
        textAlign: 'center', maxWidth: 360,
      }}>Securing your enrolment...</div>
    </div>
  );
}

// Third loading state — between Stripe success and confirm-endpoint resolve.
// Static ankh (not the meditative diamond) per the brand call: this is a
// fast post-success step, prolonging the meditative rotation here would
// extend perceived waiting unnecessarily.
function FinalisingOverlay({ attempt }) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: P.SAND, zIndex: 1000,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 24,
    }}>
      <Ankh size={48} color={P.GO} opacity={0.55} />
      <div style={{ ...T.bodyMdItalic, color: P.BR2 }}>
        Finalising your program...
      </div>
      {attempt > 1 && (
        <div style={{ ...T.bodySmItalic, color: P.TL, marginTop: 4 }}>
          Attempt {attempt}
        </div>
      )}
    </div>
  );
}

// Partial-state — Stripe charged successfully but the confirm endpoint
// kept failing through the retry budget. Calm contact-us message.
function PartialStateOverlay({ onBack }) {
  return (
    <div style={{
      width: '100vw', maxWidth: '100vw', overflowX: 'hidden',
      minHeight: '100vh', background: P.SAND,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 'clamp(40px, 6vw, 96px) 24px',
    }}>
      <div style={{ maxWidth: 540, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
          <Ankh size={48} color={P.GO} opacity={0.55} />
        </div>
        <p style={{ ...T.premise, color: P.BR2, margin: '0 0 24px' }}>
          Setting up your plan is taking a little longer than expected.
        </p>
        <p style={{ ...T.bodyMd, color: P.TL, maxWidth: 480, margin: '0 auto 28px' }}>
          We've recorded your payment. Please contact us and we'll complete setup for you.
        </p>
        <button onClick={onBack} style={{
          background: 'none', border: '1.5px solid ' + P.GO,
          color: P.BR2, fontFamily: FF, fontStyle: 'italic', fontWeight: 500,
          fontSize: 'clamp(15px, 1.2vw, 17px)',
          padding: '14px 28px', cursor: 'pointer',
        }}>Back to portal</button>
      </div>
    </div>
  );
}

// ─── Stale/redirect-back error (rare path) ───────────────────────────────
function MissingPlanState({ onBack }) {
  return (
    <div style={{
      width: '100vw', maxWidth: '100vw', overflowX: 'hidden',
      minHeight: '100vh', background: P.SAND,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 'clamp(40px, 6vw, 96px) 24px',
    }}>
      <div style={{ maxWidth: 540, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
          <Ankh size={48} color={P.GO} opacity={0.55} />
        </div>
        <p style={{ ...T.premise, color: P.BR2, margin: '0 0 28px' }}>
          Please review and accept your plan to continue to checkout.
        </p>
        <button onClick={onBack} style={{
          background: 'none', border: '1.5px solid ' + P.GO,
          color: P.BR2, fontFamily: FF, fontStyle: 'italic', fontWeight: 500,
          fontSize: 'clamp(15px, 1.2vw, 17px)',
          padding: '14px 28px', cursor: 'pointer',
        }}>Back to your plan</button>
      </div>
    </div>
  );
}

// ─── Order summary panel (left column at desktop) ────────────────────────
function OrderSummaryPanel({ checkout }) {
  if (!checkout) return null;
  const tier = TIER_NAME[checkout.bundledMembershipTier] || checkout.bundledMembershipTier;
  const isInstalment = checkout.paymentOption === 'three_instalments';
  return (
    <aside style={{
      background: P.PAP,
      border: '1px solid ' + P.SAND3,
      padding: 'clamp(28px, 3vw, 40px)',
      alignSelf: 'start',
    }}>
      <div style={{ ...T.label, color: P.BR2, marginBottom: 18 }}>YOUR ENROLMENT</div>
      <h2 style={{
        fontFamily: FF, fontSize: 'clamp(28px, 3vw, 40px)', fontWeight: 400,
        color: P.INK, margin: '0 0 22px', letterSpacing: '-0.01em', lineHeight: 1.1,
      }}>{checkout.packageDisplayName}</h2>

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px' }}>
        <li style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <Diamond size={7} color={P.GO} opacity={0.7} />
          <span style={{ ...T.bodyMd, color: P.TX }}>12-week structured program</span>
        </li>
        <li style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <Diamond size={7} color={P.GO} opacity={0.7} />
          <span style={{ ...T.bodyMd, color: P.TX }}>
            <strong style={{ color: P.TL, fontWeight: 600 }}>{tier} membership</strong>
            <span style={{ color: P.TL }}> included, activates Week 1</span>
          </span>
        </li>
        <li style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Diamond size={7} color={P.GO} opacity={0.7} />
          <span style={{ ...T.bodyMd, color: P.TX }}>
            {isInstalment ? 'Three instalments' : 'Single payment'}
          </span>
        </li>
      </ul>

      {/* Payment schedule */}
      <div style={{
        borderTop: '1px solid ' + P.SAND3,
        paddingTop: 22, marginTop: 4,
      }}>
        <div style={{ ...T.label, color: P.BR2, marginBottom: 16 }}>SCHEDULE</div>
        {(checkout.scheduleSummary || []).map(function(s, i) {
          return (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '10px 0',
              borderBottom: i < checkout.scheduleSummary.length - 1
                ? '1px solid rgba(200,184,136,0.35)' : 'none',
            }}>
              <div>
                <div style={{ ...T.bodyMd, color: P.TX, fontWeight: 500 }}>
                  {i === 0 ? 'Today' : 'Week ' + s.targetWeek}
                </div>
                {i > 0 && (
                  <div style={{ ...T.bodySmItalic, color: P.TL, marginTop: 2 }}>
                    around {shortDateFromOffset(s.dueOffsetDays)}
                  </div>
                )}
              </div>
              <div style={{ ...T.bodyMd, color: P.INK, fontWeight: 600, alignSelf: 'center' }}>
                {formatAUDFull(s.amountCents)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Total */}
      <div style={{
        marginTop: 22, paddingTop: 18,
        borderTop: '2px solid ' + P.GO,
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      }}>
        <div style={{ ...T.label, color: P.BR2 }}>TOTAL</div>
        <div style={{
          fontFamily: FF, fontSize: 'clamp(26px, 3vw, 36px)',
          fontWeight: 400, color: P.INK, letterSpacing: '-0.015em', lineHeight: 1,
        }}>
          <span style={{ fontSize: '0.7em', verticalAlign: 'baseline', marginRight: 2 }}>$</span>
          {formatDollars(checkout.totalCents)}
        </div>
      </div>
    </aside>
  );
}

// ─── Card form panel (right column at desktop) ───────────────────────────
// Uses Stripe's <Elements> + <PaymentElement>. Wallets (Apple Pay /
// Google Pay) surface natively because automatic_payment_methods is enabled
// on the PaymentIntent server-side and the device supports them.
// Lives inside the <Elements> provider so useStripe()/useElements() resolve.
// Owns the submit click; calls stripe.confirmPayment then bubbles outcome
// back to the parent state machine via three callbacks.
function CardFormPanel({ onConfirmStart, onConfirmSuccess, onConfirmError, isSubmitting, inlineError }) {
  const stripe = useStripe();
  const elements = useElements();

  async function handleSubmit() {
    if (!stripe || !elements) return; // guard — Elements not ready
    onConfirmStart();

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Used only for off-site flows (Afterpay and similar BNPL, some 3DS
        // variants). Card payments and in-iframe 3DS resolve here without
        // redirect because of redirect:'if_required' below.
        return_url: window.location.origin + '/?page=enrolment-success',
      },
      redirect: 'if_required',
    });

    if (result.error) {
      // Inline payment errors (card_declined, insufficient_funds, etc).
      // Bring the user back to the form with the message — don't change phase.
      onConfirmError(result.error.message || 'Your payment could not be completed.');
      return;
    }

    if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
      onConfirmSuccess(result.paymentIntent.id);
      return;
    }

    // Fall-through: PI didn't succeed but no error. Rare — usually means
    // a redirect-required flow that wasn't satisfied here.
    onConfirmError('Payment did not complete. Please try again.');
  }

  return (
    <section style={{
      background: P.PAP,
      border: '1px solid ' + P.SAND3,
      padding: 'clamp(28px, 3vw, 40px)',
    }}>
      <div style={{ ...T.label, color: P.BR2, marginBottom: 18 }}>PAYMENT</div>

      {/* Grace note — quiet, factual, just above the card form */}
      <div style={{
        ...T.bodyMdItalic, color: P.TL,
        marginBottom: 22, paddingBottom: 18,
        borderBottom: '1px solid rgba(200,184,136,0.35)',
      }}>
        Refundable in full within 48 hours.
      </div>

      {/* Inline error from the previous attempt, if any */}
      {inlineError && (
        <div style={{
          marginBottom: 18, padding: '12px 14px',
          background: 'rgba(192, 57, 43, 0.06)',
          borderLeft: '2px solid ' + P.RED,
          ...T.bodySmItalic, color: P.TL,
        }}>
          {inlineError}
        </div>
      )}

      <PaymentElement options={{
        layout: { type: 'tabs', defaultCollapsed: false },
        wallets: { applePay: 'auto', googlePay: 'auto' },
      }} />

      <div style={{ marginTop: 28 }}>
        <CalmCTA
          label={isSubmitting ? 'Securing...' : 'Pay and enrol'}
          onClick={handleSubmit}
          disabled={isSubmitting || !stripe || !elements}
        />
      </div>
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//                              MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════
export default function PlanCheckout(props) {
  const {
    onNavigate,
    email, devEmail,
    recommendationId,
    packageId,
    paymentOption,
    serviceEdits = [],
    addedServices = [],
  } = props;

  const effectiveEmail = email || devEmail;
  const hasRequiredInputs = !!(effectiveEmail && recommendationId && packageId && paymentOption);

  // ── ALL HOOKS UNCONDITIONAL — must run in the same order on every render ─
  // Conditional rendering happens AFTER all hook calls (rules-of-hooks).
  //
  // Phase machine:
  //   loading_checkout (initial fetch) → ready | error_checkout
  //   ready → confirming (user clicks Pay)
  //   confirming → ready (Stripe error) | finalising (Stripe success)
  //   finalising → finalising (silent retry) | success-via-onNavigate | error_partial
  const [loadingCheckout, setLoadingCheckout] = useState(true);
  const [checkout, setCheckout] = useState(null); // { clientSecret, customerId, totalCents, scheduleSummary, ... }
  const [error, setError] = useState(null);       // hard-fatal checkout-endpoint error (full-page)
  const [phase, setPhase] = useState('ready');    // 'ready' | 'confirming' | 'finalising' | 'error_partial'
  const [inlineError, setInlineError] = useState(null);   // soft errors (card declined etc) shown inside the form
  const [finalisingAttempt, setFinalisingAttempt] = useState(0);

  // ── If routed here without a plan, redirect back. Effect runs every render
  // but only does work when inputs are missing.
  useEffect(function() {
    if (!hasRequiredInputs && onNavigate) {
      const t = setTimeout(function() {
        onNavigate('plan-preview', {
          redirectMessage: 'Please review and accept your plan to continue to checkout.',
        });
      }, 60);
      return function() { clearTimeout(t); };
    }
  }, [hasRequiredInputs, onNavigate]);

  // ── Fetch the checkout payload — but only if we have the inputs to use it.
  useEffect(function() {
    if (!hasRequiredInputs) return;
    let cancelled = false;
    async function load() {
      setLoadingCheckout(true);
      setError(null);
      try {
        const res = await fetch('/api/program-plan-checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: effectiveEmail,
            recommendationId,
            packageId,
            paymentOption,
            serviceEdits,
            addedServices,
          }),
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setError(data.error || data.message || 'Could not prepare your checkout.');
          setLoadingCheckout(false);
          return;
        }
        setCheckout(data);
        setLoadingCheckout(false);
      } catch (err) {
        if (!cancelled) {
          setError('Could not connect to the server.');
          setLoadingCheckout(false);
        }
      }
    }
    load();
    return function() { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRequiredInputs]);

  // ── Stripe Elements options memo (unconditional hook) ──────────────────
  // Stripe locks options at mount — DO NOT mutate or re-create after first
  // pass. The conditional render below ensures Elements first mounts only
  // when this returns a non-null object with a real clientSecret.
  const elementsOptions = useMemo(function() {
    if (!checkout?.clientSecret) return null;
    return {
      clientSecret: checkout.clientSecret,
      appearance: buildAppearance(),
    };
  }, [checkout]);

  // ── Now safe to render conditionally ───────────────────────────────────
  if (!hasRequiredInputs) {
    return <MissingPlanState onBack={function() { onNavigate && onNavigate('plan-preview'); }} />;
  }

  // ── Real submit handlers — driven by callbacks from CardFormPanel ──────
  function handleConfirmStart() {
    setInlineError(null);
    setPhase('confirming');
  }

  function handleConfirmError(msg) {
    setPhase('ready');
    setInlineError(msg);
  }

  // Calls /api/program-plan-confirm with retry budget. Silent retries
  // every 2s (up to 6 attempts) on transient failures. Each retry surfaces
  // an attempt counter on the FinalisingOverlay. On any success, navigates
  // to enrolment-success with the full plan in routePayload. On retry
  // exhaustion, transitions to phase='error_partial' (calm contact-us).
  async function callConfirmEndpoint(paymentIntentId, attempt) {
    setPhase('finalising');
    setFinalisingAttempt(attempt);

    let body, ok = false, lastError = null;
    try {
      const res = await fetch('/api/program-plan-confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: effectiveEmail,
          paymentIntentId,
          recommendationId,
          packageId,
          paymentOption,
          serviceEdits,
          addedServices,
          clientAttempt: attempt,
          clientError: null,
        }),
      });
      body = await res.json();
      ok = res.ok && !!body.plan;
      if (!ok) lastError = body.error || body.message || 'Server returned an error';
    } catch (err) {
      lastError = err.message || 'Network error';
    }

    if (ok) {
      // Navigate to EnrolmentSuccess with the full payload — no second
      // fetch needed on landing.
      onNavigate('enrolment-success', {
        plan: body.plan,
        package: body.package,
        services: body.services,
        scheduledPayments: body.scheduledPayments,
        membership: body.membership,
        devEmail: devEmail, // thread DEV override per session-scope pattern
      });
      return;
    }

    if (attempt < 6) {
      setTimeout(function() { callConfirmEndpoint(paymentIntentId, attempt + 1); }, 2000);
      return;
    }
    // eslint-disable-next-line no-console
    console.error('[PlanCheckout] confirm endpoint failed after 6 attempts:', lastError);
    setPhase('error_partial');
  }

  function handleConfirmSuccess(paymentIntentId) {
    callConfirmEndpoint(paymentIntentId, 1);
  }

  // ── Render ─────────────────────────────────────────────────────────────
  if (loadingCheckout) return <PreparingOverlay />;

  if (error) {
    return (
      <div style={{
        width: '100vw', maxWidth: '100vw', overflowX: 'hidden',
        minHeight: '100vh', background: P.SAND,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(40px, 6vw, 96px) 24px',
      }}>
        <div style={{ maxWidth: 540, textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
            <Ankh responsive="opener" color={P.GO} />
          </div>
          <h1 style={{ ...T.displaySm, color: P.INK, margin: '0 0 18px' }}>
            <span style={{ display: 'block' }}>Your checkout</span>
            <span style={{ display: 'block', fontStyle: 'italic', color: P.BR2, fontSize: '0.9em' }}>could not be prepared.</span>
          </h1>
          <p style={{ ...T.bodyMd, color: P.TL, maxWidth: 480, margin: '0 auto 24px' }}>{error}</p>
          <button onClick={function() { onNavigate('plan-preview'); }} style={{
            background: 'none', border: '1.5px solid ' + P.GO,
            color: P.BR2, fontFamily: FF, fontStyle: 'italic', fontWeight: 500,
            fontSize: 'clamp(15px, 1.2vw, 17px)',
            padding: '12px 24px', cursor: 'pointer',
          }}>Back to your plan</button>
        </div>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div style={{
        width: '100vw', minHeight: '100vh', background: P.SAND,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}>
        <div style={{ ...T.bodyMd, color: P.RED, fontStyle: 'italic', textAlign: 'center', maxWidth: 480 }}>
          Stripe publishable key missing. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in .env and restart the dev server.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      width: '100vw', maxWidth: '100vw', overflowX: 'hidden',
      minHeight: '100vh', background: P.SAND,
      paddingBottom: 60,
    }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        padding: 'clamp(40px, 5vw, 72px) clamp(20px, 4vw, 40px)',
      }}>
        {/* Page-opener strip — calm, no big hero ankh (we already had that on PlanPreview) */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(36px, 4vw, 56px)' }}>
          <Eyebrow center>Confirming your enrolment</Eyebrow>
          <h1 style={{
            ...T.display, fontSize: 'clamp(30px, 4vw, 48px)',
            color: P.INK, margin: '20px auto 0', maxWidth: 720,
          }}>
            <span style={{ display: 'block' }}>One moment of detail,</span>
            <span style={{ display: 'block', fontStyle: 'italic', color: P.BR2, fontSize: '0.9em' }}>then your program begins.</span>
          </h1>
        </div>

        {/* Two-column at >=~780px (auto-fit/minmax), single column below */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
          gap: 'clamp(20px, 2.5vw, 36px)',
          alignItems: 'start',
        }}>
          <OrderSummaryPanel checkout={checkout} />

          {/* Gate <Elements> mount on elementsOptions being non-null. Stripe
              locks options at mount — if Elements mounts with options=null
              (the brief window where loadingCheckout has flipped false but
              checkout state hasn't propagated, or a StrictMode double-mount
              catches the null state), the later valid options are ignored
              and PaymentElement never renders its iframe. Conditional
              rendering guarantees first-mount sees valid clientSecret. */}
          {elementsOptions ? (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <CardFormPanel
                onConfirmStart={handleConfirmStart}
                onConfirmSuccess={handleConfirmSuccess}
                onConfirmError={handleConfirmError}
                isSubmitting={phase === 'confirming' || phase === 'finalising'}
                inlineError={inlineError}
              />
            </Elements>
          ) : (
            <section style={{
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
              padding: 'clamp(28px, 3vw, 40px)',
              minHeight: 200,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ ...T.bodyMdItalic, color: P.BR2 }}>
                Loading payment form...
              </div>
            </section>
          )}
        </div>

        {/* Closing line — brand-tone reassurance, calm */}
        <p style={{
          ...T.bodySmItalic, color: P.TL,
          textAlign: 'center', marginTop: 'clamp(28px, 3vw, 44px)',
          maxWidth: 540, marginLeft: 'auto', marginRight: 'auto',
        }}>
          Payments are processed by Stripe. Card details never touch our servers.
        </p>
      </div>

      {phase === 'confirming' && <SecuringOverlay />}
      {phase === 'finalising' && <FinalisingOverlay attempt={finalisingAttempt} />}
      {phase === 'error_partial' && (
        <PartialStateOverlay onBack={function() { onNavigate && onNavigate('portal'); }} />
      )}
    </div>
  );
}

// useStripe / useElements imported above for slice 2b — silenced for slice 2a.
// eslint-disable-next-line no-unused-vars
const _futureUse = { useStripe, useElements };
